import React from "react";
import LegalLayout from "../components/legal/LegalLayout";
import SEOHead from "../components/SEOHead";
import { ShieldAlert } from "lucide-react";

const SECTIONS = [
  {
    id: "overview",
    label: "1. Overview",
    content: (
      <>
        <p>
          This Acceptable Use Policy (&quot;AUP&quot;) sets out what you can
          and can&apos;t do on the Binshare platform — the website and the
          app. It supplements our{" "}
          <a
            href="/terms"
            className="text-sage-700 underline underline-offset-2"
          >
            Terms of Service
          </a>
          .
        </p>
        <p>
          Binshare&apos;s mission is to make waste collection cleaner, fairer
          and more affordable. This AUP exists to keep the marketplace safe,
          lawful, and free of fly-tipping.
        </p>
      </>
    ),
  },
  {
    id: "who-this-applies",
    label: "2. Who this applies to",
    content: (
      <p>
        This AUP applies to <strong>all users</strong> of Binshare — customers
        and carriers alike — and to anyone who interacts with the Service,
        including by browsing, posting, quoting, messaging or transacting.
      </p>
    ),
  },
  {
    id: "prohibited-waste",
    label: "3. Prohibited waste",
    content: (
      <>
        <p>
          You must not post, accept or transfer the following via Binshare:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Asbestos and asbestos-containing materials;</li>
          <li>Clinical, medical or sharps waste;</li>
          <li>Fuels, oils, paint thinners and other flammable liquids;</li>
          <li>
            Gas cylinders, pressurised containers, fireworks and explosives;
          </li>
          <li>
            Pesticides, herbicides and other regulated chemicals or batteries
            outside of council-collection schemes;
          </li>
          <li>
            Radioactive material, animal carcasses, or any other Special
            Waste / Hazardous Waste as defined by UK law.
          </li>
        </ul>
        <p>
          Specialist licensed services must be used for the above. If you
          aren&apos;t sure whether your waste qualifies, message support
          before posting.
        </p>
      </>
    ),
  },
  {
    id: "prohibited-conduct",
    label: "4. Prohibited conduct",
    content: (
      <>
        <p>You must not:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Facilitate fly-tipping</strong> or any illegal disposal of
            waste;
          </li>
          <li>
            misrepresent yourself, impersonate another person, or pose as a
            registered waste carrier without a valid waste carrier
            registration;
          </li>
          <li>
            harass, threaten, discriminate against, or intimidate other users;
          </li>
          <li>
            post content that is defamatory, obscene, hateful or otherwise
            unlawful;
          </li>
          <li>
            attempt to circumvent Binshare&apos;s fee by paying or accepting
            payment off-platform after being introduced through the Service;
          </li>
          <li>
            scrape, harvest, copy or republish listings, ratings, profiles or
            messages without our written permission;
          </li>
          <li>
            upload viruses or malicious code, probe for vulnerabilities, or
            attempt unauthorised access to the Service;
          </li>
          <li>
            spam users with unsolicited marketing, recruitment or affiliate
            offers.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "carrier-conduct",
    label: "5. Waste carrier conduct",
    content: (
      <>
        <p>In addition to the above, carriers must:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            keep their waste carrier registration current and visible on
            their profile;
          </li>
          <li>
            attend booked jobs at the agreed time or notify the customer
            promptly if they can&apos;t;
          </li>
          <li>
            complete a waste transfer note for every load and only transfer
            waste to permitted facilities;
          </li>
          <li>
            not quote with the intent to under-cut and then renegotiate on the
            day;
          </li>
          <li>
            respond to customer messages within a reasonable timeframe.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "reporting",
    label: "6. Reporting abuse",
    content: (
      <p>
        If you see content or behaviour that breaches this AUP, please report
        it using the in-app report button or email{" "}
        <a
          href="mailto:support@binshare.co.uk"
          className="text-sage-700 underline underline-offset-2"
        >
          support@binshare.co.uk
        </a>
        . We act on all credible reports.
      </p>
    ),
  },
  {
    id: "enforcement",
    label: "7. Enforcement",
    content: (
      <>
        <p>Where we believe this AUP has been breached, we may:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>remove or hide the offending content or listing;</li>
          <li>issue a warning;</li>
          <li>
            suspend or permanently terminate the account and withhold any
            pending payouts where appropriate;
          </li>
          <li>
            report the activity to the Environment Agency, the relevant
            council and / or the police; and
          </li>
          <li>
            cooperate with any investigation, including disclosing user
            information where lawfully required.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "changes",
    label: "8. Changes to this AUP",
    content: (
      <p>
        We may update this AUP from time to time. Material changes will be
        announced via the app or email at least 14 days before they take
        effect. Continued use after that date means you accept them.
      </p>
    ),
  },
  {
    id: "contact",
    label: "9. Contact us",
    content: (
      <div className="rounded-2xl border border-moss-200/50 bg-cream-100/60 p-5 sm:p-6 not-prose">
        <p className="font-semibold text-moss-900">Contact us</p>
        <p className="text-sm mt-1">
          <a
            href="mailto:support@binshare.co.uk"
            className="text-sage-700 underline underline-offset-2 font-medium"
          >
            support@binshare.co.uk
          </a>
        </p>
        <p className="text-xs text-moss-600 mt-3">
          Binshare Ltd · Company No. 16909547 · Caernarfon, Gwynedd, UK
        </p>
      </div>
    ),
  },
];

export default function AcceptableUsePage() {
  return (
    <>
      <SEOHead
        path="/acceptable-use"
        title="Acceptable Use Policy · Binshare"
        description="The rules that keep Binshare safe, lawful and free of fly-tipping — read alongside our Terms of Service."
      />
      <LegalLayout
        testidPrefix="aup"
        eyebrow={
          <>
            <ShieldAlert className="w-3.5 h-3.5" /> Legal
          </>
        }
        title="Acceptable Use Policy"
        intro="The rules that keep Binshare safe, lawful and free of fly-tipping. Please read alongside our Terms of Service."
        effective="1 February 2026"
        lastUpdated="13 October 2026"
        sections={SECTIONS}
      />
    </>
  );
}
